/**
 * Test utilities for NullEngine-based testing
 * Provides fast, reliable testing without WebGL dependencies
 */

import {
  Color4,
  HemisphericLight,
  NullEngine,
  Scene,
  type Scene as SceneType,
  UniversalCamera,
  Vector3,
} from "@babylonjs/core";
import type { SceneOptions } from "../managers/scene/types";

/**
 * Options for creating a test engine
 */
export interface TestEngineOptions {
  /**
   * Engine width
   */
  width?: number;

  /**
   * Engine height
   */
  height?: number;

  /**
   * Enable stencil buffer
   */
  stencil?: boolean;

  /**
   * Enable antialias
   */
  antialias?: boolean;
}

/**
 * Options for creating a test scene
 */
export interface TestSceneOptions extends SceneOptions {
  /**
   * Create a default camera
   */
  withCamera?: boolean;

  /**
   * Create a default light
   */
  withLight?: boolean;

  /**
   * Camera configuration
   */
  cameraConfig?: {
    name?: string;
    position?: { x: number; y: number; z: number };
  };

  /**
   * Light configuration
   */
  lightConfig?: {
    name?: string;
    intensity?: number;
  };
}

/**
 * Create a NullEngine for testing
 * Provides fast, deterministic testing without WebGL dependencies
 * 10-100x faster than WebGL-based tests
 * @param options Engine options
 * @returns NullEngine instance ready for testing
 * @example
 * ```typescript
 * const engine = createTestEngine({ width: 1024, height: 768 });
 * const scene = createTestScene(engine);
 * ```
 */
export function createTestEngine(options: TestEngineOptions = {}): NullEngine {
  const engine = new NullEngine({
    deterministicLockstep: true,
    lockstepMaxSteps: 1,
    renderHeight: options.height ?? 256,
    renderWidth: options.width ?? 512,
    textureSize: 512,
  });

  // Set additional options
  if (options.stencil !== undefined) {
    engine.setStencilBuffer(options.stencil);
  }

  return engine;
}

/**
 * Create a test scene with NullEngine
 * @param engine The engine to use
 * @param options Scene options
 */
export function createTestScene(
  engine: NullEngine,
  options: TestSceneOptions = {},
): SceneType {
  const scene = new Scene(engine);

  // Apply scene options
  if (options.clearColor) {
    scene.clearColor = new Color4(
      options.clearColor.r,
      options.clearColor.g,
      options.clearColor.b,
      options.clearColor.a ?? 1,
    );
  }

  // Add default camera if requested
  if (options.withCamera) {
    const cameraName = options.cameraConfig?.name ?? "testCamera";
    const position = options.cameraConfig?.position ?? {
      x: 0,
      y: 0,
      z: -10,
    };
    const camera = new UniversalCamera(
      cameraName,
      new Vector3(position.x, position.y, position.z),
      scene,
    );
    camera.setTarget(Vector3.Zero());
    scene.activeCamera = camera;
  }

  // Add default light if requested
  if (options.withLight) {
    const lightName = options.lightConfig?.name ?? "testLight";
    const light = new HemisphericLight(lightName, new Vector3(0, 1, 0), scene);
    if (options.lightConfig?.intensity !== undefined) {
      light.intensity = options.lightConfig.intensity;
    }
  }

  return scene;
}
